import React from 'react';
import {createPortal} from 'react-dom';
import {
    Pencil,
    Highlighter,
    Eraser,
    Undo2,
    Redo2,
    Trash2,
    GripVertical,
    ChevronDown,
    Square,
    Circle,
    Diamond,
    ArrowUpRight,
    Type,
    Lock,
    Unlock,
    Hand,
} from 'lucide-react';
import {
    AnnotationEngine,
    findTopStrokeIdAtContentPoint,
    renderShapePreview,
    renderStrokes,
} from './annotationEngine';
import {parseAnnotationMessage, serializeAnnotationMessage, type AnnotationMessage} from './protocol';
import {getSharedReceiverAnnotationEngine} from './receiverAnnotationEngineRegistry';
import {useCanvasLayout} from './useCanvasLayout';
import {getVideoRenderBox, isAnnotationDebug, normFromClientInVideo} from './videoRenderBox';
import {getWireToolForSession, shapePreviewKind, type ToolbarTool, type EraserMode} from './toolbarTypes';

type Props = {
    channel: RTCDataChannel | undefined;
    allowDraw?: boolean;
    userId?: string;
    layoutKey?: string | number;
    videoLayoutRevision?: string | number;
    canvasSyncEpoch?: number;
    onContainerRef?: (el: HTMLDivElement | null) => void;
    toolsVisible?: boolean;
    onToolsVisibleChange?: (visible: boolean) => void;
    showAnnotateButton?: boolean;
    toolbarPlacement?: 'local' | 'globalFooterCenter';
    children: React.ReactElement<{onVideoRef?: (el: HTMLVideoElement | null) => void}>;
};

const COLOR_PALETTE = ['#ff3b30', '#fbbf24', '#22c55e', '#0ea5e9', '#a855f7', '#ffffff', '#000000', '#f472b6'];

function clampToolbarPos(x: number, y: number, maxW: number, maxH: number) {
    const m = 8;
    return {
        x: Math.max(m, Math.min(Math.max(m, maxW - m), x)),
        y: Math.max(m, Math.min(Math.max(m, maxH - m), y)),
    };
}

function resolveEngine(layoutKey: string | number | undefined, privateEngineRef: React.MutableRefObject<AnnotationEngine | null>): AnnotationEngine {
    if (layoutKey !== undefined && String(layoutKey) !== '') return getSharedReceiverAnnotationEngine(String(layoutKey));
    if (!privateEngineRef.current) privateEngineRef.current = new AnnotationEngine();
    return privateEngineRef.current;
}

function newStrokeId(): string {
    return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function contentFromVideoNorm(n: {x: number; y: number}, pan: {x: number; y: number}) {
    return {x: n.x - pan.x, y: n.y - pan.y};
}

export function ReceiverAnnotationOverlay({
    channel,
    allowDraw = false,
    userId = '',
    layoutKey,
    videoLayoutRevision,
    canvasSyncEpoch = 0,
    onContainerRef,
    toolsVisible,
    onToolsVisibleChange,
    showAnnotateButton = true,
    toolbarPlacement = 'local',
    children,
}: Props) {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const privateEngineRef = React.useRef<AnnotationEngine | null>(null);
    const engine = React.useMemo(() => resolveEngine(layoutKey, privateEngineRef), [layoutKey]);
    const engineRef = React.useRef(engine);
    engineRef.current = engine;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const [videoEl, setVideoEl] = React.useState<HTMLVideoElement | null>(null);
    const [layoutTick, setLayoutTick] = React.useState(0);
    const bumpLayout = React.useCallback(() => setLayoutTick((t) => t + 1), []);
    const {resize} = useCanvasLayout(canvasRef, containerRef, bumpLayout);

    const [showToolsInternal, setShowToolsInternal] = React.useState(false);
    const showTools = toolsVisible ?? showToolsInternal;
    const setShowTools = React.useCallback(
        (next: boolean | ((prev: boolean) => boolean)) => {
            const resolved =
                typeof next === 'function' ? (next as (prev: boolean) => boolean)(showTools) : next;
            if (toolsVisible === undefined) setShowToolsInternal(resolved);
            onToolsVisibleChange?.(resolved);
        },
        [onToolsVisibleChange, showTools, toolsVisible]
    );
    const [toolbarTool, setToolbarTool] = React.useState<ToolbarTool>('pencil');
    const [eraserMode, setEraserMode] = React.useState<EraserMode>('pixel');
    const [drawLocked, setDrawLocked] = React.useState(false);
    const [color, setColor] = React.useState('#ff3b30');
    const [width, setWidth] = React.useState(3);
    const [shapePreview, setShapePreview] = React.useState<null | {tool: 'rectangle' | 'circle' | 'line' | 'diamond'; from: {x: number; y: number}; to: {x: number; y: number}}>(null);
    const [menuOpen, setMenuOpen] = React.useState<null | 'shape' | 'draw' | 'eraser' | 'color'>(null);
    const [toolbarPos, setToolbarPos] = React.useState<{x: number; y: number}>({x: 8, y: 56});
    const [toolbarDragging, setToolbarDragging] = React.useState(false);
    const [stackTick, setStackTick] = React.useState(0);
    const [textEdit, setTextEdit] = React.useState<null | {content: {x: number; y: number}; left: number; top: number}>(null);
    const [textDraft, setTextDraft] = React.useState('');
    const textDraftRef = React.useRef('');
    const textEditRef = React.useRef<null | {content: {x: number; y: number}; left: number; top: number}>(null);
    textDraftRef.current = textDraft;
    textEditRef.current = textEdit;

    const strokeIdRef = React.useRef<string | null>(null);
    const phaseRef = React.useRef<'idle' | 'free' | 'shape' | 'pan'>('idle');
    const pendingPointsRef = React.useRef<Array<{x: number; y: number}>>([]);
    const rafRef = React.useRef<number | null>(null);
    const shapeStartRef = React.useRef<null | {x: number; y: number}>(null);
    const panLastClientRef = React.useRef<null | {x: number; y: number}>(null);
    const panStrokeIdRef = React.useRef<string | null>(null);
    const panMoveTotalRef = React.useRef({dx: 0, dy: 0});
    const undoGroupsRef = React.useRef<string[][]>([]);
    const redoGroupsRef = React.useRef<string[][]>([]);
    const currentStrokeLogRef = React.useRef<string[]>([]);
    const hiddenColorInputRef = React.useRef<HTMLInputElement | null>(null);
    const dragRef = React.useRef({active: false, startX: 0, startY: 0, originX: 8, originY: 56});
    const toolbarRef = React.useRef<HTMLDivElement | null>(null);
    const [toolbarHasManualPos, setToolbarHasManualPos] = React.useState(false);

    const setContainerEl = React.useCallback(
        (el: HTMLDivElement | null) => {
            containerRef.current = el;
            onContainerRef?.(el);
        },
        [onContainerRef]
    );

    const child = React.Children.only(children) as React.ReactElement<{onVideoRef?: (el: HTMLVideoElement | null) => void}>;
    const videoChild = React.cloneElement(child, {
        onVideoRef: (el: HTMLVideoElement | null) => {
            videoRef.current = el;
            setVideoEl(el);
            child.props.onVideoRef?.(el);
        },
    });

    const render = React.useCallback(() => {
        const canvas = canvasRef.current;
        const box = containerRef.current;
        const video = videoRef.current;
        if (!canvas || !box || !video) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const map = getVideoRenderBox(video);
        renderStrokes(ctx, engineRef.current.getStrokes(), {canvasW: box.clientWidth, canvasH: box.clientHeight, map}, {
            debug: isAnnotationDebug(),
            highlightUserId: userId,
            layerPan: engineRef.current.getLayerPan(),
        });
        if (shapePreview) {
            renderShapePreview(ctx, shapePreview.tool, color, width, 1, shapePreview.from, shapePreview.to, map, engineRef.current.getLayerPan());
        }
    }, [color, shapePreview, userId, width]);

    const sendAndApply = React.useCallback((msg: AnnotationMessage, opts?: {pushGroup?: boolean; persistOnly?: boolean}) => {
        const raw = serializeAnnotationMessage(msg);
        if (channel?.readyState === 'open') channel.send(raw);
        engineRef.current.apply(msg);
        render();
        if (opts?.pushGroup) currentStrokeLogRef.current.push(raw);
        if (opts?.persistOnly) currentStrokeLogRef.current = [raw];
    }, [channel, render]);

    const finishStrokeGroup = React.useCallback(() => {
        const log = currentStrokeLogRef.current;
        if (log.length > 0) {
            undoGroupsRef.current.push(log);
            redoGroupsRef.current = [];
            setStackTick((t) => t + 1);
        }
        currentStrokeLogRef.current = [];
    }, []);

    const wireOpacityForTool = React.useCallback((t: ToolbarTool) => {
        const wt = getWireToolForSession(t, eraserMode);
        if (!wt) return 1;
        return wt === 'highlighter' ? 0.35 : 1;
    }, [eraserMode]);

    React.useEffect(() => {
        if (!videoEl) return;
        const onVid = () => bumpLayout();
        videoEl.addEventListener('loadedmetadata', onVid);
        videoEl.addEventListener('resize', onVid);
        window.addEventListener('orientationchange', onVid);
        document.addEventListener('fullscreenchange', onVid);
        return () => {
            videoEl.removeEventListener('loadedmetadata', onVid);
            videoEl.removeEventListener('resize', onVid);
            window.removeEventListener('orientationchange', onVid);
            document.removeEventListener('fullscreenchange', onVid);
        };
    }, [videoEl, bumpLayout]);

    React.useLayoutEffect(() => {
        resize();
        render();
    }, [layoutKey, layoutTick, canvasSyncEpoch, videoLayoutRevision, resize, render, videoEl, shapePreview]);

    React.useEffect(() => {
        if (!channel) return;
        const onMsg = (ev: MessageEvent<string>) => {
            const raw = typeof ev.data === 'string' ? ev.data : String(ev.data);
            const msg = parseAnnotationMessage(raw);
            if (!msg) return;
            if (msg.type === 'sync') engineRef.current.clear();
            engineRef.current.apply(msg);
            requestAnimationFrame(render);
        };
        channel.addEventListener('message', onMsg);
        return () => channel.removeEventListener('message', onMsg);
    }, [channel, render]);

    const flushPendingDraw = React.useCallback(() => {
        rafRef.current = null;
        const sid = strokeIdRef.current;
        if (!sid || phaseRef.current !== 'free' || !userId) {
            pendingPointsRef.current = [];
            return;
        }
        const wt = getWireToolForSession(toolbarTool, eraserMode);
        if (!wt) return;
        const pts = pendingPointsRef.current;
        if (pts.length === 0) return;
        const chunk = pts.splice(0, Math.min(pts.length, 48));
        sendAndApply({
            type: 'draw',
            userId,
            tool: wt,
            color,
            width,
            strokeId: sid,
            points: chunk,
            opacity: wireOpacityForTool(toolbarTool),
        }, {pushGroup: true});
        if (pendingPointsRef.current.length > 0) rafRef.current = requestAnimationFrame(flushPendingDraw);
    }, [color, eraserMode, sendAndApply, toolbarTool, userId, width, wireOpacityForTool]);

    const scheduleFlush = React.useCallback(() => {
        if (rafRef.current != null) return;
        rafRef.current = requestAnimationFrame(flushPendingDraw);
    }, [flushPendingDraw]);

    const canAnnotate = allowDraw && !!userId;

    React.useEffect(() => {
        const mergedShowTools = toolsVisible ?? showToolsInternal;
        const active = canAnnotate && mergedShowTools;
        console.log('[receiver-annotation:overlay]', {
            layoutKey,
            toolbarPlacement,
            toolsVisibleProp: toolsVisible,
            showToolsInternal,
            mergedShowTools,
            allowDraw,
            hasUserId: Boolean(userId),
            canAnnotate,
            toolsActive: active,
            hint: !userId && mergedShowTools
                ? 'Toolbar hidden: pass annotationUserId (meUserId) — canAnnotate is false'
                : !mergedShowTools
                  ? 'mergedShowTools false — parent toolsVisible && annotationActive must match this tile'
                  : undefined,
        });
    }, [
        layoutKey,
        toolbarPlacement,
        toolsVisible,
        showToolsInternal,
        allowDraw,
        userId,
        canAnnotate,
    ]);

    const onPointerDown = React.useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!canAnnotate || !showTools || !videoRef.current) return;
        if (drawLocked && toolbarTool !== 'pan') return;
        if (toolbarTool === 'pan') {
            e.preventDefault();
            const n = normFromClientInVideo(videoRef.current, e.clientX, e.clientY);
            const c = contentFromVideoNorm(n, engineRef.current.getLayerPan());
            const hit = findTopStrokeIdAtContentPoint(engineRef.current.getStrokes(), c);
            if (!hit) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            phaseRef.current = 'pan';
            panStrokeIdRef.current = hit;
            panMoveTotalRef.current = {dx: 0, dy: 0};
            panLastClientRef.current = {x: e.clientX, y: e.clientY};
            return;
        }
        if (toolbarTool === 'text') {
            e.preventDefault();
            const box = containerRef.current?.getBoundingClientRect();
            if (!box) return;
            const n = normFromClientInVideo(videoRef.current, e.clientX, e.clientY);
            const content = contentFromVideoNorm(n, engineRef.current.getLayerPan());
            const fieldW = Math.min(280, Math.max(140, box.width - 8));
            const left = Math.min(Math.max(4, e.clientX - box.left), Math.max(4, box.width - fieldW - 4));
            const top = Math.min(Math.max(4, e.clientY - box.top), Math.max(4, box.height - 40));
            setTextEdit({content, left, top});
            setTextDraft('');
            return;
        }
        if (toolbarTool === 'eraser' && eraserMode === 'element') {
            e.preventDefault();
            const pn = normFromClientInVideo(videoRef.current, e.clientX, e.clientY);
            const c = contentFromVideoNorm(pn, engineRef.current.getLayerPan());
            const hit = findTopStrokeIdAtContentPoint(engineRef.current.getStrokes(), c);
            if (hit) sendAndApply({type: 'undo', userId, strokeId: hit});
            return;
        }
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const p = normFromClientInVideo(videoRef.current, e.clientX, e.clientY);
        const sk = shapePreviewKind(toolbarTool);
        if (sk) {
            phaseRef.current = 'shape';
            shapeStartRef.current = contentFromVideoNorm(p, engineRef.current.getLayerPan());
            setShapePreview({tool: sk, from: shapeStartRef.current, to: shapeStartRef.current});
            strokeIdRef.current = null;
            return;
        }
        const wt = getWireToolForSession(toolbarTool, eraserMode);
        if (!wt) return;
        phaseRef.current = 'free';
        const strokeId = newStrokeId();
        strokeIdRef.current = strokeId;
        pendingPointsRef.current = [];
        sendAndApply({
            type: 'start',
            userId,
            tool: wt,
            color,
            width,
            strokeId,
            opacity: wireOpacityForTool(toolbarTool),
        }, {pushGroup: true});
    }, [canAnnotate, color, drawLocked, eraserMode, sendAndApply, showTools, toolbarTool, userId, width, wireOpacityForTool]);

    const onPointerMove = React.useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!videoRef.current) return;
        if (phaseRef.current === 'pan' && panLastClientRef.current && panStrokeIdRef.current) {
            const map = getVideoRenderBox(videoRef.current);
            const last = panLastClientRef.current;
            const dx = e.clientX - last.x;
            const dy = e.clientY - last.y;
            panLastClientRef.current = {x: e.clientX, y: e.clientY};
            const dnx = dx / map.renderWidth;
            const dny = dy / map.renderHeight;
            panMoveTotalRef.current.dx += dnx;
            panMoveTotalRef.current.dy += dny;
            engineRef.current.tryMoveStrokeDelta(panStrokeIdRef.current, dnx, dny, userId);
            render();
            return;
        }
        const pan = engineRef.current.getLayerPan();
        if (phaseRef.current === 'shape' && shapeStartRef.current) {
            const p = normFromClientInVideo(videoRef.current, e.clientX, e.clientY);
            setShapePreview((prev) => (prev ? {...prev, from: shapeStartRef.current as {x: number; y: number}, to: contentFromVideoNorm(p, pan)} : null));
            return;
        }
        if (phaseRef.current !== 'free' || !strokeIdRef.current) return;
        pendingPointsRef.current.push(contentFromVideoNorm(normFromClientInVideo(videoRef.current, e.clientX, e.clientY), pan));
        scheduleFlush();
    }, [render, scheduleFlush, userId]);

    const onPointerUp = React.useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (phaseRef.current === 'pan') {
            phaseRef.current = 'idle';
            panLastClientRef.current = null;
            const movedId = panStrokeIdRef.current;
            panStrokeIdRef.current = null;
            const tot = panMoveTotalRef.current;
            panMoveTotalRef.current = {dx: 0, dy: 0};
            if (movedId && (Math.abs(tot.dx) > 1e-10 || Math.abs(tot.dy) > 1e-10)) {
                sendAndApply({type: 'strokeMove', userId, strokeId: movedId, dx: tot.dx, dy: tot.dy});
            }
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
            render();
            return;
        }
        if (phaseRef.current === 'shape' && shapeStartRef.current && videoRef.current) {
            const sk = shapePreviewKind(toolbarTool);
            if (!sk) return;
            const to = contentFromVideoNorm(normFromClientInVideo(videoRef.current, e.clientX, e.clientY), engineRef.current.getLayerPan());
            const msg: AnnotationMessage = {type: 'shape', userId, tool: sk, color, width, strokeId: newStrokeId(), from: shapeStartRef.current, to, opacity: 1};
            sendAndApply(msg, {persistOnly: true});
            finishStrokeGroup();
            shapeStartRef.current = null;
            setShapePreview(null);
            phaseRef.current = 'idle';
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
            return;
        }
        if (phaseRef.current === 'free' && strokeIdRef.current) {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            flushPendingDraw();
            sendAndApply({type: 'end', userId, strokeId: strokeIdRef.current}, {pushGroup: true});
            finishStrokeGroup();
            strokeIdRef.current = null;
            pendingPointsRef.current = [];
            phaseRef.current = 'idle';
        }
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    }, [color, finishStrokeGroup, flushPendingDraw, render, sendAndApply, toolbarTool, userId, width]);

    const commitTextLabel = React.useCallback(() => {
        const anchor = textEditRef.current?.content;
        const trimmed = textDraftRef.current.trim();
        if (!anchor || !trimmed) {
            setTextEdit(null);
            setTextDraft('');
            return;
        }
        sendAndApply({
            type: 'text',
            userId,
            strokeId: newStrokeId(),
            x: anchor.x,
            y: anchor.y,
            text: trimmed,
            color,
            fontSize: Math.max(10, Math.min(48, Math.round(10 + width * 2.2))),
        }, {persistOnly: true});
        finishStrokeGroup();
        setTextEdit(null);
        setTextDraft('');
    }, [color, finishStrokeGroup, sendAndApply, userId, width]);

    const onClear = React.useCallback(() => {
        if (!userId) return;
        sendAndApply({type: 'clear', userId, all: true});
        undoGroupsRef.current = [];
        redoGroupsRef.current = [];
        currentStrokeLogRef.current = [];
        setStackTick((t) => t + 1);
    }, [sendAndApply, userId]);

    const onUndo = React.useCallback(() => {
        if (!userId) return;
        const last = undoGroupsRef.current.pop();
        if (!last?.length) return;
        const first = parseAnnotationMessage(last[0]);
        const strokeId = first?.type === 'start' || first?.type === 'shape' || first?.type === 'text' ? first.strokeId : undefined;
        redoGroupsRef.current.push(last);
        setStackTick((t) => t + 1);
        sendAndApply({type: 'undo', userId, strokeId});
    }, [sendAndApply, userId]);

    const onRedo = React.useCallback(() => {
        if (!userId) return;
        const group = redoGroupsRef.current.pop();
        if (!group?.length) return;
        undoGroupsRef.current.push(group);
        setStackTick((t) => t + 1);
        const first = parseAnnotationMessage(group[0]);
        const strokeId = first?.type === 'start' || first?.type === 'shape' || first?.type === 'text' ? first.strokeId : newStrokeId();
        const messages = group.map((raw) => parseAnnotationMessage(raw)).filter(Boolean) as AnnotationMessage[];
        sendAndApply({type: 'redo', userId, strokeId, messages});
    }, [sendAndApply, userId]);

    const canUndo = React.useMemo(() => undoGroupsRef.current.length > 0, [stackTick]);
    const canRedo = React.useMemo(() => redoGroupsRef.current.length > 0, [stackTick]);
    const toolsActive = canAnnotate && showTools;
    const iconBtnClass =
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-white/10 bg-[rgba(255,255,255,0.03)] text-white/85 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed';
    const activeBtnClass =
        'border-cyan-300 bg-cyan-500/45 text-white shadow-[0_0_0_2px_rgba(34,211,238,0.45),0_0_18px_rgba(34,211,238,0.35)]';
    const splitMainClass = 'rounded-r-none border-r-0';
    const splitMenuClass = 'w-[22px] rounded-l-none';

    React.useLayoutEffect(() => {
        if (!toolsActive || toolbarPlacement !== 'globalFooterCenter' || toolbarHasManualPos) return;
        const el = toolbarRef.current;
        if (!el) return;
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        setToolbarPos({
            x: Math.max(8, Math.round((window.innerWidth - w) / 2)),
            y: Math.max(8, window.innerHeight - h - 72),
        });
    }, [toolsActive, toolbarPlacement, toolbarHasManualPos]);

    React.useEffect(() => {
        if (!showTools) return;
        setToolbarHasManualPos(false);
        if (toolbarPlacement !== 'globalFooterCenter') {
            setToolbarPos({x: 8, y: 56});
        }
    }, [showTools, toolbarPlacement]);

    const onGripDown = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = toolbarRef.current?.getBoundingClientRect();
        const originX = rect ? rect.left : toolbarPos.x;
        const originY = rect ? rect.top : toolbarPos.y;
        dragRef.current = {active: true, startX: e.clientX, startY: e.clientY, originX, originY};
        setToolbarDragging(true);
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    }, [toolbarPos.x, toolbarPos.y]);

    const onGripMove = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
        if (!dragRef.current.active) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        let next;
        if (toolbarPlacement === 'globalFooterCenter') {
            const el = toolbarRef.current;
            const w = el?.offsetWidth ?? 360;
            const h = el?.offsetHeight ?? 44;
            next = clampToolbarPos(
                dragRef.current.originX + dx,
                dragRef.current.originY + dy,
                window.innerWidth - w - 8,
                window.innerHeight - h - 8
            );
        } else {
            if (!containerRef.current) return;
            next = clampToolbarPos(
                dragRef.current.originX + dx,
                dragRef.current.originY + dy,
                containerRef.current.clientWidth - 16,
                containerRef.current.clientHeight - 16
            );
        }
        setToolbarHasManualPos(true);
        setToolbarPos(next);
    }, [toolbarPlacement]);

    const onGripUp = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
        if (!dragRef.current.active) return;
        dragRef.current.active = false;
        setToolbarDragging(false);
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    }, []);

    React.useEffect(() => () => {
        if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    }, []);

    /** Toolbar UI: portaled to `document.body` in global mode so video tiles’ `overflow-hidden` cannot clip it. */
    const toolbarUi = toolsActive ? (
            <div
                ref={toolbarRef}
                className={`flex max-w-[calc(100vw-24px)] flex-nowrap items-center gap-1.5 overflow-visible rounded-full border border-white/10 bg-[rgba(16,16,22,0.92)] px-2.5 py-1.5 text-white shadow-[0_8px_32px_rgba(0,0,0,0.45)] backdrop-blur-md ${
                    toolbarPlacement === 'globalFooterCenter'
                        ? 'fixed z-[500] justify-start'
                        : 'absolute z-[44] max-w-[calc(100%-16px)] justify-start'
                }`}
                style={{left: toolbarPos.x, top: toolbarPos.y}}
            >
                    <button
                        type="button"
                        className="inline-flex h-8 w-[22px] shrink-0 items-center justify-center rounded-[8px] bg-transparent text-white/70"
                        style={{cursor: toolbarDragging ? 'grabbing' : 'grab'}}
                        onPointerDown={onGripDown}
                        onPointerMove={onGripMove}
                        onPointerUp={onGripUp}
                        onPointerCancel={onGripUp}
                    >
                        <GripVertical size={14} />
                    </button>
                    <div className="h-5 w-px shrink-0 bg-white/15" />
                    <button
                        type="button"
                        className={`${iconBtnClass} ${drawLocked ? activeBtnClass : ''}`}
                        onClick={() => setDrawLocked((v) => !v)}
                    >
                        {drawLocked ? <Lock size={16} /> : <Unlock size={16} />}
                    </button>
                    <button
                        type="button"
                        className={`${iconBtnClass} ${toolbarTool === 'pan' ? activeBtnClass : ''}`}
                        onClick={() => setToolbarTool('pan')}
                    >
                        <Hand size={16} />
                    </button>
                    <div className="relative flex items-center">
                        <button
                            type="button"
                            className={`${iconBtnClass} ${splitMainClass} ${['rectangle','diamond','circle','arrow'].includes(toolbarTool) ? activeBtnClass : ''}`}
                            onClick={() =>
                                setToolbarTool((t) =>
                                    ['rectangle', 'diamond', 'circle', 'arrow'].includes(t) ? t : 'rectangle'
                                )
                            }
                        >
                            {toolbarTool === 'diamond' ? (
                                <Diamond size={16} />
                            ) : toolbarTool === 'circle' ? (
                                <Circle size={16} />
                            ) : toolbarTool === 'arrow' ? (
                                <ArrowUpRight size={16} />
                            ) : (
                                <Square size={16} />
                            )}
                        </button>
                        <button
                            type="button"
                            className={`${iconBtnClass} ${splitMenuClass} ${['rectangle','diamond','circle','arrow'].includes(toolbarTool) ? activeBtnClass : ''}`}
                            onClick={() => setMenuOpen((m) => (m === 'shape' ? null : 'shape'))}
                        >
                            <ChevronDown size={13} />
                        </button>
                        {menuOpen === 'shape' && (
                            <div className="absolute bottom-full left-0 z-[50] mb-1.5 grid grid-cols-2 gap-1 rounded-lg border border-white/20 bg-black/90 p-2 shadow-lg">
                                {[{id: 'rectangle', Icon: Square}, {id: 'diamond', Icon: Diamond}, {id: 'circle', Icon: Circle}, {id: 'arrow', Icon: ArrowUpRight}].map(({id, Icon}) => (
                                    <button key={id} type="button" className={`rounded p-1 ${toolbarTool === id ? 'bg-cyan-500/60' : 'bg-white/10'}`} onClick={() => { setToolbarTool(id as ToolbarTool); setMenuOpen(null);}}><Icon size={14} /></button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="relative flex items-center">
                        <button
                            type="button"
                            className={`${iconBtnClass} ${splitMainClass} ${toolbarTool === 'pencil' || toolbarTool === 'highlighter' ? activeBtnClass : ''}`}
                            onClick={() =>
                                setToolbarTool((t) => (t === 'pencil' || t === 'highlighter' ? t : 'pencil'))
                            }
                        >
                            {toolbarTool === 'highlighter' ? <Highlighter size={16} /> : <Pencil size={16} />}
                        </button>
                        <button
                            type="button"
                            className={`${iconBtnClass} ${splitMenuClass} ${toolbarTool === 'pencil' || toolbarTool === 'highlighter' ? activeBtnClass : ''}`}
                            onClick={() => setMenuOpen((m) => (m === 'draw' ? null : 'draw'))}
                        >
                            <ChevronDown size={13} />
                        </button>
                        {menuOpen === 'draw' && (
                            <div className="absolute bottom-full left-0 z-[50] mb-1.5 flex gap-1 rounded-lg border border-white/20 bg-black/90 p-2 shadow-lg">
                                <button type="button" className={`rounded p-1 ${toolbarTool === 'pencil' ? 'bg-cyan-500/60' : 'bg-white/10'}`} onClick={() => { setToolbarTool('pencil'); setMenuOpen(null);}}><Pencil size={14} /></button>
                                <button type="button" className={`rounded p-1 ${toolbarTool === 'highlighter' ? 'bg-cyan-500/60' : 'bg-white/10'}`} onClick={() => { setToolbarTool('highlighter'); setMenuOpen(null);}}><Highlighter size={14} /></button>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className={`${iconBtnClass} ${toolbarTool === 'text' ? activeBtnClass : ''}`}
                        onClick={() => setToolbarTool('text')}
                    >
                        <Type size={16} />
                    </button>
                    <div className="relative flex items-center">
                        <button
                            type="button"
                            className={`${iconBtnClass} ${splitMainClass} ${toolbarTool === 'eraser' ? activeBtnClass : ''}`}
                            onClick={() => setToolbarTool('eraser')}
                        >
                            <Eraser size={16} />
                        </button>
                        <button
                            type="button"
                            className={`${iconBtnClass} ${splitMenuClass} ${toolbarTool === 'eraser' ? activeBtnClass : ''}`}
                            onClick={() => setMenuOpen((m) => (m === 'eraser' ? null : 'eraser'))}
                        >
                            <ChevronDown size={13} />
                        </button>
                        {menuOpen === 'eraser' && (
                            <div className="absolute bottom-full left-0 z-[50] mb-1.5 flex gap-1 rounded-lg border border-white/20 bg-black/90 p-2 text-xs shadow-lg">
                                <button type="button" className={`rounded px-2 py-1 ${eraserMode === 'pixel' ? 'bg-cyan-500/60' : 'bg-white/10'}`} onClick={() => { setEraserMode('pixel'); setToolbarTool('eraser'); setMenuOpen(null);}}>Pixel</button>
                                <button type="button" className={`rounded px-2 py-1 ${eraserMode === 'element' ? 'bg-cyan-500/60' : 'bg-white/10'}`} onClick={() => { setEraserMode('element'); setToolbarTool('eraser'); setMenuOpen(null);}}>Element</button>
                                <button type="button" className="rounded bg-white/10 px-2 py-1" onClick={() => { setMenuOpen(null); onClear();}}>Clear</button>
                            </div>
                        )}
                    </div>
                    <div className="h-5 w-px shrink-0 bg-white/15" />
                    <div className="relative shrink-0">
                    <button
                        type="button"
                        className="h-7 w-7 shrink-0 rounded-full border-2 border-black/35"
                        style={{background: color, boxShadow: '0 0 0 2px #fff, 0 0 0 4px rgba(14,165,233,0.35)'}}
                        onClick={() => setMenuOpen((m) => (m === 'color' ? null : 'color'))}
                    />
                    {menuOpen === 'color' && (
                        <div className="absolute bottom-full right-0 z-[50] mb-1.5 rounded-lg border border-white/20 bg-black/90 p-2 shadow-lg">
                            <div className="grid grid-cols-4 gap-1">
                                {COLOR_PALETTE.map((c) => (
                                    <button key={c} type="button" className="h-5 w-5 rounded-full border border-white/20" style={{background: c}} onClick={() => setColor(c)} />
                                ))}
                            </div>
                            <button type="button" className="mt-2 w-full rounded bg-white/10 px-2 py-1 text-xs" onClick={() => hiddenColorInputRef.current?.click()}>Custom</button>
                        </div>
                    )}
                    </div>
                    <input ref={hiddenColorInputRef} type="color" value={color} onChange={(e) => setColor(e.target.value)} className="hidden" />
                    <input type="range" min={1} max={24} value={width} onChange={(e) => setWidth(Number(e.target.value))} className="w-[68px] accent-cyan-400" />
                    <div className="h-5 w-px shrink-0 bg-white/15" />
                    <button type="button" className={iconBtnClass} disabled={!canUndo || drawLocked} onClick={onUndo}><Undo2 size={17} /></button>
                    <button type="button" className={iconBtnClass} disabled={!canRedo || drawLocked} onClick={onRedo}><Redo2 size={17} /></button>
                    <button type="button" className={iconBtnClass} onClick={onClear}><Trash2 size={17} /></button>
                    <div className="h-5 w-px shrink-0 bg-white/15" />
                    <button
                        type="button"
                        className="inline-flex h-8 shrink-0 items-center gap-1 rounded-[9px] border border-white/20 bg-white/10 px-2.5 text-xs font-semibold text-white"
                        onClick={() => {
                            setMenuOpen(null);
                            setShowTools(false);
                        }}
                    >
                        Exit
                    </button>
                </div>
        ) : null;

    return (
        <>
            {toolbarPlacement === 'globalFooterCenter' && toolbarUi
                ? createPortal(toolbarUi, document.body)
                : null}
            <div ref={setContainerEl} className="relative h-full min-h-0 w-full">
            {videoChild}
            {allowDraw && showAnnotateButton && (
                <button
                    type="button"
                    className="absolute left-2 top-2 z-[14] inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-[#6f6f72]/90 px-4 py-2 text-[18px] font-medium text-white shadow-[0_10px_24px_rgba(0,0,0,0.35)] backdrop-blur-md"
                    onClick={() => setShowTools((v) => !v)}
                    style={{display: showTools ? 'none' : 'inline-flex'}}
                >
                    <Pencil size={20} />
                    <span>Annotate</span>
                </button>
            )}
            {toolbarPlacement !== 'globalFooterCenter' ? toolbarUi : null}
            {textEdit && (
                <input
                    type="text"
                    value={textDraft}
                    placeholder="Text"
                    onChange={(ev) => setTextDraft(ev.target.value)}
                    onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                            ev.preventDefault();
                            commitTextLabel();
                        }
                        if (ev.key === 'Escape') {
                            ev.preventDefault();
                            setTextEdit(null);
                            setTextDraft('');
                        }
                    }}
                    onBlur={() => {
                        if (textDraftRef.current.trim()) commitTextLabel();
                        else {
                            setTextEdit(null);
                            setTextDraft('');
                        }
                    }}
                    className="absolute z-[19] rounded border border-white/25 bg-black/90 px-2 py-1 text-sm text-white"
                    style={{left: textEdit.left, top: textEdit.top, minWidth: 140, maxWidth: 280}}
                />
            )}
            <canvas
                ref={canvasRef}
                className={`absolute left-0 top-0 z-[5] h-full w-full ${toolsActive ? 'pointer-events-auto' : 'pointer-events-none'}`}
                style={{touchAction: 'none', cursor: toolsActive ? 'crosshair' : 'default'}}
                aria-hidden
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            />
        </div>
        </>
    );
}
